/**
 * Google Analytics 4 (gtag.js) — consent-gated.
 *
 * GA only loads AFTER the visitor accepts cookies (localStorage
 * `mtc-cookie-consent === 'accepted'`), to stay Loi 09-08 / GDPR friendly.
 * Page views are sent manually on each SPA route change (auto page_view is
 * disabled in the config), and custom events flow through `gaEvent`.
 *
 * This complements the Firestore counters in `track.ts`: GA handles visitor
 * counts / sources / devices / funnel; Firestore powers the in-admin
 * "most-viewed formations" panel. The `trackEvent` helper fires both.
 */

const GA_MEASUREMENT_ID = 'G-6C8C9E3V58';
const CONSENT_KEY = 'mtc-cookie-consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialised = false;

export function hasAnalyticsConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    return false;
  }
}

/**
 * Inject gtag.js and configure GA4. Idempotent and a no-op without consent,
 * so it's safe to call on app load AND the moment the user clicks "Accepter".
 */
export function initAnalytics(): void {
  if (initialised) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  initialised = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // Push the live arguments object — matches Google's canonical shim.
    // eslint-disable-next-line prefer-rest-params
    (window.dataLayer as unknown[]).push(arguments);
  };
  window.gtag('js', new Date());
  // Manual page_view on route change (this is an SPA).
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Send the first page view for the entry route.
  gaPageView(window.location.pathname + window.location.search);
}

/** Send a manual SPA page view. No-op until GA is initialised. */
export function gaPageView(path: string): void {
  if (!initialised || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** Send a custom GA4 event. No-op until GA is initialised. */
export function gaEvent(name: string, params?: Record<string, unknown>): void {
  if (!initialised || !window.gtag) return;
  window.gtag('event', name, params ?? {});
}
